import React, {createContext, useContext, useEffect, useState} from 'react';
import axios from 'axios';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {AtzmaiUsersApiService} from '../../../../services';
import {AppDispatch, RootState} from '../../../../store/configureStore';
import {setToken, setUser} from '../../../../modules/Auth';
import SplashScreen from '../../ui/SplashScreen';
import Spinner from '../../ui/Spinner';
import {useMessage} from '../useGlobalMessage';
import {useGlobalMessageContext} from '../GlobalMessageContext';
import {logoutChannel} from '../../../utils';
import {CurrentUserDetails} from '../../../types';
import {pathnames as P} from '../../../enums';

export type UserSessionContext = {
  currentUser: CurrentUserDetails | null
  paymentPageNotAllowed: boolean
  invoiceOrderPageNotAllowed: boolean
  handleLogout: () => void
}

export interface GlobalContextProviderProps {
  children: React.ReactNode;
}

export const defaultContext: UserSessionContext = {
  currentUser: null,
  paymentPageNotAllowed: false,
  invoiceOrderPageNotAllowed: false,
  handleLogout: () => {},
};

export const UserSessionContext = createContext<UserSessionContext>(defaultContext);
UserSessionContext.displayName = 'UserSessionContext';

export const useUserSessionContext = () => useContext(UserSessionContext);

export const UserSessionContextProvider = ({children}: GlobalContextProviderProps) => {
  const navigate = useNavigate();
  const E = useMessage();
  const currentToken = useSelector((state: RootState) => state.auth.token) as string;
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentLocale = useSelector((state: RootState) => state.i18n.locale);
  const globalMessage = useGlobalMessageContext();
  const [loading, setLoading] = useState(true);

  const goBack = () => {
    axios.defaults.headers.common['token'] = null;
    navigate(P.LOGIN);
  };

  const apiService = new AtzmaiUsersApiService(currentToken);
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    if (!currentToken) {
      goBack();
    }
  }, [currentToken]);

  useEffect(() => {
    if (currentToken) {
      setLoading(true);
      updateUser()
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    logoutChannel.addEventListener('message', () => {
      void logout();
    });
  }, []);

  useEffect(() => {
    if (currentUser && !currentUser.agentId && currentToken && !loading) {
      const params = new URLSearchParams({
        token: currentToken,
        regType: 'all',
        language: currentLocale
      });
      window.location.href = `${process.env.REACT_APP_REG_FORM_DOMAIN}${process.env.REACT_APP_REG_FORM_PATH}?${params.toString()}`;
    }
  }, [currentUser?.agentId, currentToken, loading]);

  const updateUser = async () => {
    try {
      dispatch(setUser(null));
      const result = await apiService.getCurrentUser();
      if (result.errorCode < 0) {
        globalMessage.open(E.serverError);
      }
      dispatch(setUser(result.body.currentUser));
    } catch (e) {
      globalMessage.open(E.serverError);
      void logout();
    }
  };

  const handleLogout = async () => {
    await logoutChannel.postMessage('Logout');
    await logout();
  };

  const logout = async () => {
    dispatch(setUser(null));
    dispatch(setToken(''));
    apiService.resetToken();
    setTimeout(() => {
      navigate(P.LOGIN);
    }, 500);
  };

  const context: UserSessionContext = {
    ...defaultContext,
    currentUser,
    paymentPageNotAllowed: Boolean(currentUser?.paymentPageNotAllowed),
    invoiceOrderPageNotAllowed: Boolean(currentUser?.invoiceOrderPageNotAllowed),
    handleLogout,
  };

  useEffect(() => {
    if (loading) return;
    if (!currentToken || !currentUser?.agentId) {
      void handleLogout();
    }
  }, [currentToken, currentUser, loading])

  if (loading) {
    return <Spinner/>;
  } else {
    return (currentToken && currentUser?.agentId)
      ? (
        <UserSessionContext.Provider value={context}>
          {children}
        </UserSessionContext.Provider>
      )
      : <SplashScreen size={'full'}/>;
  }
};
